import React, {useCallback, useMemo} from "react";
import styled from "@emotion/styled";
import FilterItem, {getCustomValueKey} from "./FilterItem";
import {Button, Popover} from "@blueprintjs/core";
import {StyledSelectButton} from "../StyledButtons";
import {v4 as uuidv4} from "uuid";
import {TReadyDueFilter, TReadyDueFilterItem} from "./types";

export type ReadyDueFilterModalProps = {
  filter: TReadyDueFilter;
  maxNumberOfFilters: number;
  onFilterChange: (filter: TReadyDueFilter) => void;
};

export const isValidFilter = (item: TReadyDueFilterItem) => {
  return item.stopType !== undefined && item.timeType !== undefined && item.timeValue !== undefined;
};

const ReadyDueFilter = ({filter, onFilterChange, maxNumberOfFilters}: ReadyDueFilterModalProps) => {
  const validFilters = useMemo(() => filter.items.filter(isValidFilter), [filter.items]);

  const handleAddFilter = useCallback(() => {
    const newFilter: TReadyDueFilterItem = {
      id: uuidv4(),
      stopType: undefined,
      timeType: undefined,
      timeValue: undefined
    };
    onFilterChange({...filter, items: [...filter.items, newFilter]});
  }, [filter, onFilterChange]);

  const handleUpdateFilter = useCallback(
    (id: string, updatedItem: TReadyDueFilterItem, customValue: number) => {
      const customValueKey = getCustomValueKey(updatedItem);
      onFilterChange({
        ...filter,
        items: filter.items.map((filter) => (filter.id === id ? updatedItem : filter)),
        customValues: {
          ...filter.customValues,
          ...(customValueKey ? {[customValueKey]: customValue} : {})
        }
      });
    },
    [filter, onFilterChange]
  );

  const handleClearFilters = useCallback(() => {
    onFilterChange({...filter, items: []});
  }, [filter, onFilterChange]);

  const handleDeleteFilter = useCallback(
    (id: string) => {
      onFilterChange({...filter, items: filter.items.filter((filter) => filter.id !== id)});
    },
    [filter, onFilterChange]
  );

  const onModalClosing = useCallback(() => {
    if (validFilters.length > 0) {
      onFilterChange({...filter, items: validFilters});
    } else {
      onFilterChange({
        ...filter,
        items: [
          {
            id: uuidv4(),
            stopType: undefined,
            timeType: undefined,
            timeValue: undefined
          }
        ]
      });
    }
  }, [filter, onFilterChange, validFilters]);

  const isAddButtonDisabled = useMemo(
    () => filter.items.length >= maxNumberOfFilters,
    [filter.items.length, maxNumberOfFilters]
  );

  return (
    <Popover
      content={
        <StyledContainer>
          <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
            {filter.items.map((filterItem) => {
              return (
                <FilterItem
                  key={filterItem.id}
                  filter={filter}
                  filterItem={filterItem}
                  onUpdate={handleUpdateFilter}
                  onDelete={handleDeleteFilter}
                />
              );
            })}
          </div>
          <div style={{display: "flex", justifyContent: "space-between", gap: "8px"}}>
            <Button
              onClick={handleAddFilter}
              disabled={isAddButtonDisabled}
              icon="plus"
              style={{borderRadius: "6px", border: "1px solid #cbd0df"}}
              variant="minimal"
              intent="primary"
              data-testid="add-filter-btn"
            />
            {validFilters.length > 0 && (
              <Button
                onClick={handleClearFilters}
                style={{borderRadius: "6px"}}
                variant="minimal"
                intent="danger"
                text="Clear Filters"
                data-testid="clear-filters-btn"
              />
            )}
          </div>
        </StyledContainer>
      }
      onClosing={onModalClosing}
      minimal
      position="bottom"
      modifiers={{offset: {enabled: true, options: {offset: [100, 5]}}}}
    >
      <StyledSelectButton
        variant="minimal"
        endIcon="chevron-down"
        hasValue={validFilters.length > 0}
        data-testid="ready-due-filter-btn"
        style={{width: "110px"}}
      >
        {validFilters.length > 0 ? `${validFilters.length} Filter${validFilters.length > 1 ? "s" : ""}` : "Ready / Due"}
      </StyledSelectButton>
    </Popover>
  );
};

export default ReadyDueFilter;

const StyledContainer = styled.div`
  font-family: "Roboto", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  min-width: 600px;
`;
